/**
 * Diminova — popup de oferta desde el carrito (drawer)
 * - Al cerrar el drawer de pago → popup a los 10s (si aún hay ítems)
 * - Al pulsar basura/eliminar → popup inmediato
 */
(function () {
  'use strict';

  if (window.__diminovaCheckoutReturnBound) return;
  window.__diminovaCheckoutReturnBound = true;

  const KEY_SHOWN_AT = 'diminova_cr_last_shown_at';
  const KEY_OFFER = 'diminova_cr_cached_offer';
  const COOLDOWN_MS = 60 * 1000; // no spamear: máx 1 vez por minuto
  const CLOSE_DELAY_MS = 10 * 1000;

  let modalEl = null;
  let open = false;
  let closeTimer = null;
  let suggestPromise = null;
  let memoryOffer = null;

  function storeGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function storeSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (_) {}
  }

  function recentlyShown() {
    const at = Number(storeGet(KEY_SHOWN_AT) || 0);
    return at && Date.now() - at < COOLDOWN_MS;
  }

  function markShown() {
    storeSet(KEY_SHOWN_AT, String(Date.now()));
  }

  function cacheOffer(offer) {
    if (!offer || !(Number(offer.now) > 0)) return offer;
    memoryOffer = offer;
    try {
      storeSet(KEY_OFFER, JSON.stringify(offer));
    } catch (_) {}
    return offer;
  }

  function getCachedOffer() {
    if (memoryOffer && Number(memoryOffer.now) > 0) return memoryOffer;
    try {
      const raw = storeGet(KEY_OFFER);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && Number(parsed.now) > 0) {
        memoryOffer = parsed;
        return parsed;
      }
    } catch (_) {}
    return null;
  }

  function moneyFromCents(cents) {
    const raw = Number(cents);
    if (!Number.isFinite(raw)) return '0';
    const major = Math.round(raw / 100);
    return major.toLocaleString('es-CL', { maximumFractionDigits: 0 });
  }

  function moneyLabel(cents) {
    return moneyFromCents(cents) + ' CLP';
  }

  function moneyFromMajor(amount) {
    const n = Number(amount) || 0;
    return Math.round(n).toLocaleString('es-CL', { maximumFractionDigits: 0 }) + ' CLP';
  }

  function productPriceLabel(p) {
    if (p.price_min != null) return moneyLabel(p.price_min);
    if (typeof p.price === 'number') return moneyLabel(p.price);
    const variantPrice = p.variants?.[0]?.price;
    if (variantPrice != null && variantPrice !== '') return moneyFromMajor(variantPrice);
    if (typeof p.price === 'string' && p.price !== '') return moneyFromMajor(p.price);
    return '';
  }

  async function fetchProductByHandle(handle) {
    if (!handle) return null;
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  function pickVariantCompare(product, variantId) {
    if (!product) return 0;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const match = variants.find((v) => String(v.id) === String(variantId)) || variants[0];
    const compare = Number(match?.compare_at_price || product.compare_at_price || 0);
    const price = Number(match?.price || product.price || 0);
    return compare > price ? compare : 0;
  }

  function readAnchors() {
    try {
      const el = document.getElementById('diminova-price-anchors');
      if (!el) return {};
      const data = JSON.parse(el.textContent || '{}');
      return data && typeof data === 'object' ? data : {};
    } catch (_) {
      return {};
    }
  }

  function buildOfferRecord({ title, image, now, compare }) {
    const safeNow = Number(now) || 0;
    if (!(safeNow > 0)) return null;
    let safeCompare = Number(compare) || 0;
    if (!(safeCompare > safeNow)) {
      safeCompare = safeNow > 100000 ? Math.max(3599000, Math.round(safeNow / 0.72)) : Math.round(safeNow / 0.72);
    }
    if (!(safeCompare > safeNow)) safeCompare = Math.round(safeNow * 1.35);
    const savings = Math.max(0, safeCompare - safeNow);
    const pct = safeCompare > 0 ? Math.max(1, Math.round((savings / safeCompare) * 100)) : 0;
    return {
      title: title || 'Producto en tu carrito',
      image: image || '',
      now: safeNow,
      compare: safeCompare,
      savings,
      pct,
      hasDeal: true,
    };
  }

  function offerFromAnchor(anchor) {
    if (!anchor) return null;
    return buildOfferRecord({
      title: anchor.title,
      image: anchor.image,
      now: anchor.now || (Number(anchor.unit_now) || 0) * (Number(anchor.qty) || 1),
      compare: anchor.compare || (Number(anchor.unit_compare) || 0) * (Number(anchor.qty) || 1),
    });
  }

  function offerFromAnchors() {
    const values = Object.values(readAnchors());
    for (let i = 0; i < values.length; i++) {
      const offer = offerFromAnchor(values[i]);
      if (offer) return offer;
    }
    return null;
  }

  function offerFromHero() {
    const hero = document.querySelector('[data-diminova-price-hero]');
    if (!hero) return null;
    const now = Number(hero.getAttribute('data-price')) || 0;
    const compare = Number(hero.getAttribute('data-compare')) || 0;
    const title =
      document.querySelector('.product-title, .product-details h1, h1')?.textContent?.trim() ||
      'Producto en oferta';
    const image =
      document.querySelector('.product-media img, media-gallery img, .product-information img')?.getAttribute(
        'src'
      ) || '';
    return buildOfferRecord({ title, image, now, compare });
  }

  function findAnchorForItem(item, anchors) {
    if (!item || !anchors) return null;
    if (item.key && anchors[item.key]) return anchors[item.key];
    const values = Object.values(anchors);
    return (
      values.find((a) => String(a.variant_id) === String(item.variant_id)) ||
      values.find((a) => String(a.product_id) === String(item.product_id)) ||
      values.find((a) => a.handle && a.handle === item.handle) ||
      null
    );
  }

  function itemImageUrl(item, anchor) {
    if (anchor?.image && typeof anchor.image === 'string') return anchor.image;
    if (!item) return '';
    if (typeof item.image === 'string' && item.image) return item.image;
    if (item.featured_image) {
      if (typeof item.featured_image === 'string') return item.featured_image;
      if (item.featured_image.url) return item.featured_image.url;
      if (item.featured_image.src) return item.featured_image.src;
    }
    return '';
  }

  function lineNowFromItem(item, cart) {
    if (!item) return 0;
    const qty = Number(item.quantity) || 1;
    const candidates = [
      item.final_line_price,
      item.line_price,
      item.original_line_price,
      item.discounted_price != null ? Number(item.discounted_price) * qty : 0,
      item.price != null ? Number(item.price) * qty : 0,
      cart?.total_price,
      cart?.items_subtotal_price,
    ];
    for (let i = 0; i < candidates.length; i++) {
      const n = Number(candidates[i]);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 0;
  }

  async function resolveOfferPricing(cart) {
    const item = cart?.items?.[0];
    if (!item) return null;

    const qty = Number(item.quantity) || 1;
    const anchors = readAnchors();
    const anchor = findAnchorForItem(item, anchors);

    let now = Number(anchor?.now) || lineNowFromItem(item, cart);
    let compare = Number(anchor?.compare) || 0;

    if (!(compare > now)) {
      const unitCompare = Number(anchor?.unit_compare) || 0;
      if (unitCompare > 0) compare = unitCompare * qty;
    }

    if (!(compare > now)) {
      const hero = offerFromHero();
      if (hero && Number(hero.compare) > now) compare = Number(hero.compare);
    }

    if (!(compare > now)) {
      const itemCompare = Number(item.compare_at_price) || 0;
      if (itemCompare > 0) compare = itemCompare * qty;
      else if (Number(item.original_line_price) > now) compare = Number(item.original_line_price);
    }

    if (!(compare > now)) {
      const product = await fetchProductByHandle(item.handle);
      const unitCompare = pickVariantCompare(product, item.variant_id);
      if (unitCompare > 0) compare = unitCompare * qty;
      else if (product && Number(product.price) > 0 && !(now > 0)) {
        now = Number(product.price) * qty;
      }
    }

    return cacheOffer(
      buildOfferRecord({
        title: anchor?.title || item.product_title || item.title || 'Producto en tu carrito',
        image: itemImageUrl(item, anchor),
        now,
        compare,
      })
    );
  }

  async function refreshOfferCache() {
    const cart = await fetchCart();
    let offer = await resolveOfferPricing(cart);
    if (!offer) offer = offerFromAnchors();
    if (!offer) offer = offerFromHero();
    if (!offer) offer = getCachedOffer();
    if (offer) cacheOffer(offer);
    return offer;
  }

  async function resolveOfferForModal() {
    const cart = await fetchCart();
    let offer = await resolveOfferPricing(cart);
    if (!offer || !(offer.now > 0)) offer = getCachedOffer();
    if (!offer || !(offer.now > 0)) offer = offerFromAnchors();
    if (!offer || !(offer.now > 0)) offer = offerFromHero();
    if (offer) cacheOffer(offer);
    return offer;
  }

  function fillOfferPricing(offer) {
    const el = ensureModal();
    const block = el.querySelector('[data-cr-price]');
    const compareRow = el.querySelector('[data-cr-compare-row]');
    const compareEl = el.querySelector('[data-cr-compare]');
    const nowEl = el.querySelector('[data-cr-now]');
    const saveEl = el.querySelector('[data-cr-save]');
    const pctEl = el.querySelector('[data-cr-pct]');
    const nameEl = el.querySelector('[data-cr-product-name]');
    const imgEl = el.querySelector('[data-cr-product-img]');
    const msgEl = el.querySelector('[data-cr-cta-msg]');
    const btn = el.querySelector('[data-cr-checkout]');
    const titleEl = el.querySelector('#diminova-cr-title');
    const offerEl = el.querySelector('[data-cr-offer]');

    if (btn) btn.textContent = 'Comprar ahora';

    if (!offer || !block || !(Number(offer.now) > 0)) {
      if (block) block.hidden = true;
      if (titleEl) titleEl.textContent = 'Oferta solo por hoy';
      if (offerEl) offerEl.textContent = 'Precio especial · hasta agotar stock';
      if (msgEl) msgEl.textContent = 'Aprovecha ahora. Esta condición no se repetirá.';
      return;
    }

    block.hidden = false;
    if (compareRow) compareRow.hidden = false;

    if (nameEl) nameEl.textContent = offer.title || 'Producto en tu carrito';

    if (imgEl) {
      if (offer.image) {
        imgEl.src = offer.image;
        imgEl.alt = offer.title || '';
        imgEl.hidden = false;
      } else {
        imgEl.removeAttribute('src');
        imgEl.hidden = true;
      }
    }

    if (compareEl) compareEl.textContent = moneyLabel(offer.compare);
    if (nowEl) nowEl.textContent = moneyFromCents(offer.now);

    if (saveEl) {
      saveEl.hidden = false;
      saveEl.textContent = 'Ahorras ' + moneyLabel(offer.savings);
    }

    if (pctEl) {
      if (offer.pct > 0) {
        pctEl.hidden = false;
        pctEl.textContent = '-' + offer.pct + '%';
      } else {
        pctEl.hidden = true;
      }
    }

    // Copy retail: escasez + beneficio, sin repetir los precios del bloque
    if (titleEl) titleEl.textContent = 'Oferta solo por hoy';
    if (offerEl) offerEl.textContent = 'Precio especial · hasta agotar stock';
    if (msgEl) {
      msgEl.textContent =
        'Aprovecha esta oferta ahora. Quedan pocas unidades a este precio — no te quedes sin la tuya.';
    }
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  function getBubbleCount() {
    const el = document.querySelector(
      '.header-actions__cart-icon .cart-bubble__text-count, .header-actions__cart-icon [ref="cartBubbleCount"]'
    );
    return Number(el?.textContent || 0);
  }

  async function cartHasItems() {
    const bubble = getBubbleCount();
    if (bubble > 0) return true;
    const cart = await fetchCart();
    return !!(cart && cart.item_count > 0);
  }

  async function fetchRelated(productId) {
    const exclude = productId ? String(productId) : null;
    let products = [];

    if (productId) {
      try {
        const res = await fetch(
          '/recommendations/products.json?product_id=' +
            encodeURIComponent(productId) +
            '&limit=4&intent=related',
          { credentials: 'same-origin' }
        );
        if (res.ok) {
          const data = await res.json();
          products = Array.isArray(data.products) ? data.products : [];
        }
      } catch (_) {}
    }

    if (!products.length) {
      try {
        const res = await fetch('/collections/all/products.json?limit=12', {
          credentials: 'same-origin',
        });
        if (res.ok) {
          const data = await res.json();
          products = Array.isArray(data.products) ? data.products : [];
        }
      } catch (_) {}
    }

    const others = exclude ? products.filter((p) => String(p.id) !== exclude) : products;
    return (others.length ? others : products).slice(0, 4);
  }

  function isCartDrawerOpen() {
    const drawer = document.querySelector('#cart-drawer, cart-drawer-component, theme-drawer#cart-drawer');
    if (!drawer) return false;
    if (drawer.hasAttribute('open')) return true;
    const dialog = drawer.matches('dialog') ? drawer : drawer.querySelector('dialog');
    return !!(dialog && dialog.open);
  }

  function cancelCloseTimer() {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  function schedulePopupAfterClose() {
    cancelCloseTimer();
    // Guardar precios YA, mientras el carrito todavía tiene ítems
    refreshOfferCache();
    closeTimer = setTimeout(async () => {
      closeTimer = null;
      if (open) return;
      if (isCartDrawerOpen()) return;
      if (!(await cartHasItems()) && !getCachedOffer()) return;
      openModal({ force: !!getCachedOffer() });
    }, CLOSE_DELAY_MS);
  }

  function ensureModal() {
    if (modalEl && document.body.contains(modalEl)) return modalEl;
    modalEl = null;

    const wrap = document.createElement('div');
    wrap.className = 'diminova-cr-overlay';
    wrap.setAttribute('role', 'presentation');
    wrap.innerHTML =
      '<div class="diminova-cr-modal" role="dialog" aria-modal="true" aria-labelledby="diminova-cr-title">' +
      '<div class="diminova-cr-modal__glow" aria-hidden="true"></div>' +
      '<button type="button" class="diminova-cr-close" aria-label="Cerrar" data-cr-close>' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<div class="diminova-cr-body">' +
      '<p class="diminova-cr-eyebrow">Oferta limitada</p>' +
      '<h2 class="diminova-cr-title" id="diminova-cr-title">Oferta solo por hoy</h2>' +
      '<p class="diminova-cr-offer" data-cr-offer>Precio especial · hasta agotar stock</p>' +
      '<div class="diminova-cr-price" data-cr-price hidden>' +
      '<div class="diminova-cr-price__product">' +
      '<img class="diminova-cr-price__img" data-cr-product-img alt="" width="64" height="64" hidden>' +
      '<div class="diminova-cr-price__product-text">' +
      '<p class="diminova-cr-price__name" data-cr-product-name></p>' +
      '<span class="diminova-cr-price__pct" data-cr-pct hidden></span>' +
      '</div>' +
      '</div>' +
      '<div class="diminova-cr-price__compare-row" data-cr-compare-row>' +
      '<span class="diminova-cr-price__label">Antes</span>' +
      '<s class="diminova-cr-price__compare" data-cr-compare></s>' +
      '</div>' +
      '<div class="diminova-cr-price__now-row">' +
      '<span class="diminova-cr-price__label diminova-cr-price__label--now">Ahora</span>' +
      '<span class="diminova-cr-price__amount" data-cr-now></span>' +
      '<span class="diminova-cr-price__currency">CLP</span>' +
      '</div>' +
      '<span class="diminova-cr-price__badge" data-cr-save></span>' +
      '</div>' +
      '<p class="diminova-cr-question" data-cr-cta-msg>Aprovecha esta oferta ahora. Quedan pocas unidades a este precio — no te quedes sin la tuya.</p>' +
      '<div class="diminova-cr-actions">' +
      '<button type="button" class="diminova-cr-btn diminova-cr-btn--primary" data-cr-checkout>Comprar ahora</button>' +
      '<button type="button" class="diminova-cr-btn diminova-cr-btn--secondary" data-cr-browse>Ver similares</button>' +
      '</div>' +
      '<div class="diminova-cr-suggest" data-cr-suggest>' +
      '<p class="diminova-cr-suggest__title">También te puede interesar</p>' +
      '<div class="diminova-cr-suggest__grid" data-cr-grid></div>' +
      '<p class="diminova-cr-suggest__loading" data-cr-loading>Buscando sugerencias…</p>' +
      '</div>' +
      '</div></div>';

    document.body.appendChild(wrap);
    modalEl = wrap;

    wrap.addEventListener('click', (e) => {
      if (e.target === wrap || e.target.closest('[data-cr-close]')) closeModal();
    });
    wrap.querySelector('[data-cr-checkout]')?.addEventListener('click', goToCheckout);
    wrap.querySelector('[data-cr-browse]')?.addEventListener('click', showSuggestions);

    return wrap;
  }

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  }

  async function openModal({ force } = { force: false }) {
    if (open) return;
    if (!force && recentlyShown()) return;
    if (!(await cartHasItems()) && !force && !getCachedOffer() && !offerFromAnchors() && !offerFromHero()) {
      return;
    }

    cancelCloseTimer();
    const el = ensureModal();
    open = true;
    el.classList.add('is-open');
    lockScroll(true);
    markShown();

    try {
      window.__diminovaCartTempt?.stop?.();
    } catch (_) {}

    const offer = await resolveOfferForModal();
    fillOfferPricing(offer);

    prefetchSuggestions();
  }

  function closeModal() {
    if (!modalEl) return;
    open = false;
    modalEl.classList.remove('is-open');
    lockScroll(false);
  }

  function goToCheckout() {
    closeModal();
    // Open cart drawer first if checkout button lives there
    const trigger = document.querySelector(
      '[data-testid="cart-drawer-trigger"], button[aria-controls="cart-drawer"], .header-actions__cart-icon'
    );
    const btn = document.querySelector('#checkout, button[name="checkout"], button.cart__checkout-button');
    if (btn && !btn.disabled && btn.offsetParent !== null) {
      btn.click();
      return;
    }
    if (trigger) {
      trigger.click();
      setTimeout(() => {
        const checkout = document.querySelector('#checkout, button[name="checkout"], button.cart__checkout-button');
        if (checkout && !checkout.disabled) checkout.click();
        else window.location.href = '/checkout';
      }, 450);
      return;
    }
    window.location.href = '/checkout';
  }

  function prefetchSuggestions() {
    suggestPromise = (async () => {
      const cart = await fetchCart();
      const productId = cart?.items?.[0]?.product_id;
      return fetchRelated(productId);
    })();
    return suggestPromise;
  }

  async function showSuggestions() {
    const el = ensureModal();
    const box = el.querySelector('[data-cr-suggest]');
    const grid = el.querySelector('[data-cr-grid]');
    const loading = el.querySelector('[data-cr-loading]');
    if (!box || !grid) return;

    box.classList.add('is-visible');
    if (loading) loading.style.display = '';

    const products = await prefetchSuggestions();
    if (loading) loading.style.display = 'none';

    if (!products.length) {
      grid.innerHTML =
        '<p class="diminova-cr-suggest__empty">Explora el catálogo — hay más tecnología esperándote.</p>' +
        '<a class="diminova-cr-btn diminova-cr-btn--secondary" href="/collections/all" style="display:inline-flex;margin-top:10px;text-decoration:none;">Ver catálogo</a>';
      return;
    }

    grid.innerHTML = products
      .slice(0, 4)
      .map((p) => {
        const imgSrc =
          (typeof p.featured_image === 'string' && p.featured_image) ||
          (p.featured_image && p.featured_image.src) ||
          (p.images && (typeof p.images[0] === 'string' ? p.images[0] : p.images[0]?.src)) ||
          '';
        const href = p.url ? p.url : '/products/' + (p.handle || '');
        const price = productPriceLabel(p);
        const name = p.title || 'Producto';
        return (
          '<a class="diminova-cr-card" href="' +
          href +
          '">' +
          (imgSrc
            ? '<img class="diminova-cr-card__img" src="' +
              imgSrc +
              '" alt="" loading="lazy" width="160" height="160">'
            : '<div class="diminova-cr-card__img" aria-hidden="true"></div>') +
          '<span class="diminova-cr-card__name">' +
          escapeHtml(name) +
          '</span>' +
          (price ? '<span class="diminova-cr-card__price">' + escapeHtml(price) + '</span>' : '') +
          '</a>'
        );
      })
      .join('');
  }

  function isCartDrawerNode(node) {
    if (!node || !(node instanceof Element)) return false;
    return !!(
      node.id === 'cart-drawer' ||
      node.matches?.('theme-drawer#cart-drawer, #cart-drawer, cart-drawer-component') ||
      node.closest?.('#cart-drawer, theme-drawer#cart-drawer')
    );
  }

  // 1) Cerrar drawer de pago → popup a los 10s
  document.addEventListener(
    'theme-drawer:close',
    (event) => {
      if (!isCartDrawerNode(event.target)) return;
      schedulePopupAfterClose();
    },
    true
  );

  // Backup: MutationObserver on cart drawer open attribute / dialog
  function watchDrawer() {
    const drawer =
      document.querySelector('#cart-drawer') ||
      document.querySelector('theme-drawer#cart-drawer') ||
      document.querySelector('cart-drawer-component');
    if (!drawer || drawer.dataset.diminovaCrWatch === 'true') return;
    drawer.dataset.diminovaCrWatch = 'true';

    let wasOpen = isCartDrawerOpen();
    const mo = new MutationObserver(() => {
      const nowOpen = isCartDrawerOpen();
      if (wasOpen && !nowOpen) schedulePopupAfterClose();
      if (nowOpen) cancelCloseTimer();
      wasOpen = nowOpen;
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['open', 'aria-hidden', 'class'] });
    const dialog = drawer.querySelector('dialog') || (drawer.matches('dialog') ? drawer : null);
    if (dialog) mo.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  // 2) Basura / eliminar ítem → snapshot de precios + popup inmediato
  document.addEventListener(
    'click',
    (event) => {
      const removeBtn = event.target.closest(
        '.cart-items__remove, button[aria-label*="Eliminar" i], button[aria-label*="Remove" i], button[aria-label*="quitar" i]'
      );
      if (!removeBtn) return;
      if (!removeBtn.closest('#cart-drawer, cart-drawer-component, .cart-items, cart-items-component, .cart-page')) {
        return;
      }
      cancelCloseTimer();
      // Capturar oferta ANTES de que Shopify vacíe el carrito
      refreshOfferCache().finally(() => {
        setTimeout(() => openModal({ force: true }), 280);
      });
    },
    true
  );

  // If user opens drawer again, cancel pending popup and refresh price cache
  document.addEventListener(
    'theme-drawer:open',
    (event) => {
      if (!isCartDrawerNode(event.target)) return;
      cancelCloseTimer();
      refreshOfferCache();
    },
    true
  );

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && open) closeModal();
  });

  function boot() {
    watchDrawer();
    // Sembrar cache desde Liquid / ficha de producto
    const seeded = offerFromAnchors() || offerFromHero() || getCachedOffer();
    if (seeded) cacheOffer(seeded);
    refreshOfferCache();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);

  window.__diminovaCheckoutReturn = {
    open: () => openModal({ force: true }),
    close: closeModal,
    schedule: schedulePopupAfterClose,
    test: () => openModal({ force: true }),
    refresh: refreshOfferCache,
  };
})();
